import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../../components/ui/card';
import { DataTable, DataTableColumn } from '../../../components/ui/data-table';
import { Button } from '../../../components/ui/button';
import PageHeader from '../../../components/common/PageHeader';
import {
  History,
  User,
  DollarSign,
  Activity,
  FileText,
  Download,
} from 'lucide-react';

type AuditLog = {
  id: string;
  action:
    | 'investor_joined'
    | 'investor_left'
    | 'trade_copied'
    | 'deposit'
    | 'withdrawal'
    | 'fee_charged'
    | 'strategy_created'
    | 'fund_created';
  user: string;
  target: string;
  amount?: number;
  service: 'copy_trading' | 'pamm' | 'mam';
  timestamp: string;
  ipAddress: string;
  status: 'success' | 'failed' | 'pending';
};

const AdminAuditLogs = () => {
  const [serviceFilter, setServiceFilter] = useState<
    'all' | 'copy_trading' | 'pamm' | 'mam'
  >('all');

  const auditLogs: AuditLog[] = [
    {
      id: 'LOG001',
      action: 'investor_joined',
      user: 'John Smith',
      target: 'Gold Scalper Pro',
      service: 'copy_trading',
      timestamp: '2024-01-15 10:30:00',
      ipAddress: '192.168.1.100',
      status: 'success',
    },
    {
      id: 'LOG002',
      action: 'trade_copied',
      user: 'Jane Doe',
      target: 'EURUSD',
      service: 'copy_trading',
      amount: 15000,
      timestamp: '2024-01-15 09:45:00',
      ipAddress: '192.168.1.101',
      status: 'success',
    },
    {
      id: 'LOG003',
      action: 'deposit',
      user: 'Mike Johnson',
      target: 'Alpha Growth Fund',
      service: 'pamm',
      amount: 25000,
      timestamp: '2024-01-15 08:20:00',
      ipAddress: '192.168.1.102',
      status: 'success',
    },
    {
      id: 'LOG004',
      action: 'fee_charged',
      user: 'Platform',
      target: 'Sarah Williams',
      service: 'copy_trading',
      amount: 1250,
      timestamp: '2024-01-15 07:55:00',
      ipAddress: '192.168.1.103',
      status: 'success',
    },
    {
      id: 'LOG005',
      action: 'investor_left',
      user: 'David Brown',
      target: 'Conservative Income',
      service: 'mam',
      timestamp: '2024-01-15 07:30:00',
      ipAddress: '192.168.1.104',
      status: 'success',
    },
    {
      id: 'LOG006',
      action: 'withdrawal',
      user: 'Emily Chen',
      target: 'FX Income Fund',
      service: 'pamm',
      amount: 5000,
      timestamp: '2024-01-15 06:45:00',
      ipAddress: '192.168.1.105',
      status: 'pending',
    },
    {
      id: 'LOG007',
      action: 'strategy_created',
      user: 'Robert Wilson',
      target: 'Crypto Alpha',
      service: 'copy_trading',
      timestamp: '2024-01-15 05:30:00',
      ipAddress: '192.168.1.106',
      status: 'success',
    },
    {
      id: 'LOG008',
      action: 'fund_created',
      user: 'Lisa Anderson',
      target: 'High Yield Fund',
      service: 'pamm',
      timestamp: '2024-01-15 04:15:00',
      ipAddress: '192.168.1.107',
      status: 'success',
    },
  ];

  const filteredLogs = auditLogs.filter(
    log => serviceFilter === 'all' || log.service === serviceFilter
  );

  const auditColumns: DataTableColumn<AuditLog>[] = [
    {
      id: 'id',
      header: 'ID',
      accessor: row => <span className="font-medium">{row.id}</span>,
      sortable: true,
      sortValue: row => row.id,
      width: '100px',
    },
    {
      id: 'action',
      header: 'Action',
      accessor: row => (
        <div className="flex items-center gap-2">
          {row.action === 'investor_joined' && (
            <User className="h-4 w-4 text-green-600" />
          )}
          {row.action === 'investor_left' && (
            <User className="h-4 w-4 text-red-600" />
          )}
          {row.action === 'trade_copied' && (
            <Activity className="h-4 w-4 text-blue-600" />
          )}
          {row.action === 'deposit' && (
            <DollarSign className="h-4 w-4 text-green-600" />
          )}
          {row.action === 'withdrawal' && (
            <DollarSign className="h-4 w-4 text-red-600" />
          )}
          {row.action === 'fee_charged' && (
            <FileText className="h-4 w-4 text-yellow-600" />
          )}
          {row.action === 'strategy_created' && (
            <Activity className="h-4 w-4 text-purple-600" />
          )}
          {row.action === 'fund_created' && (
            <Activity className="h-4 w-4 text-purple-600" />
          )}
          <span className="text-sm font-medium">
            {row.action.replace('_', ' ').toUpperCase()}
          </span>
        </div>
      ),
      sortable: true,
      sortValue: row => row.action,
      width: '200px',
    },
    {
      id: 'user',
      header: 'User',
      accessor: row => row.user,
      sortable: true,
      sortValue: row => row.user,
      width: '150px',
    },
    {
      id: 'target',
      header: 'Target',
      accessor: row => row.target,
      sortable: true,
      sortValue: row => row.target,
      width: '180px',
    },
    {
      id: 'amount',
      header: 'Amount',
      accessor: row => (row.amount ? `$${row.amount.toLocaleString()}` : '-'),
      sortable: true,
      sortValue: row => row.amount || 0,
      width: '120px',
    },
    {
      id: 'service',
      header: 'Service',
      accessor: row => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.service === 'copy_trading'
              ? 'bg-blue-100 text-blue-800'
              : row.service === 'pamm'
                ? 'bg-purple-100 text-purple-800'
                : 'bg-orange-100 text-orange-800'
          }`}
        >
          {row.service.replace('_', ' ').toUpperCase()}
        </span>
      ),
      sortable: true,
      sortValue: row => row.service,
      width: '120px',
    },
    {
      id: 'timestamp',
      header: 'Timestamp',
      accessor: row => new Date(row.timestamp).toLocaleString(),
      sortable: true,
      sortValue: row => row.timestamp,
      width: '180px',
    },
    {
      id: 'ipAddress',
      header: 'IP Address',
      accessor: row => row.ipAddress,
      sortable: true,
      sortValue: row => row.ipAddress,
      width: '140px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.status === 'success'
              ? 'bg-green-100 text-green-800'
              : row.status === 'pending'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-red-100 text-red-800'
          }`}
        >
          {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.status,
      width: '100px',
    },
  ];

  const todayCount = auditLogs.filter(log => {
    const logDate = new Date(log.timestamp);
    const today = new Date();
    return logDate.toDateString() === today.toDateString();
  }).length;

  const successCount = auditLogs.filter(log => log.status === 'success').length;
  const pendingCount = auditLogs.filter(log => log.status === 'pending').length;
  const failedCount = auditLogs.filter(log => log.status === 'failed').length;

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Audit Logs"
          description="Track all activities across trading services"
        />
      </div>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant={serviceFilter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setServiceFilter('all')}
          >
            All Services
          </Button>
          <Button
            variant={serviceFilter === 'copy_trading' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setServiceFilter('copy_trading')}
          >
            Copy Trading
          </Button>
          <Button
            variant={serviceFilter === 'pamm' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setServiceFilter('pamm')}
          >
            PAMM
          </Button>
          <Button
            variant={serviceFilter === 'mam' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setServiceFilter('mam')}
          >
            MAM
          </Button>
        </div>
        <Button variant="outline" size="sm">
          <Download className="mr-2 h-4 w-4" />
          Export Logs
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-blue-600" />
              Today&apos;s Logs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{todayCount}</div>
            <p className="text-sm text-muted-foreground mt-2">
              Activities today
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-green-600" />
              Successful
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              {successCount}
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Completed actions
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-yellow-600" />
              Pending
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-yellow-600">
              {pendingCount}
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Awaiting completion
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-red-600" />
              Failed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600">{failedCount}</div>
            <p className="text-sm text-muted-foreground mt-2">Failed actions</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Audit Log History</CardTitle>
          <CardDescription>
            Complete activity log across all trading services
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={auditColumns}
            data={filteredLogs}
            searchPlaceholder="Search logs..."
            showColumnVisibility={true}
            showExportButton={false}
            showAddButton={false}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAuditLogs;
